import React, { useCallback, useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { isBooksConnected } from "../../domain/steps";
import { useCoClient } from "../../data/store";
import { fetchZohoRecords } from "../../data/zohoRecordsApi";
import { fetchOutstandingImport, type OutstandingImportDTO, type OutstandingKind } from "../../data/outstandingApi";
import { StatusCard, type CardStatus } from "../../components/StatusCard";
import { SubStepShell } from "../../components/SubStepShell";
import { OpeningBalanceGrid } from "../opening-balances/OpeningBalanceGrid";
import { OpeningBalanceImportPanel } from "../opening-balances/OpeningBalanceImportPanel";
import { OutstandingImportPanel } from "../outstanding-import/OutstandingImportPanel";
import { useSubStepNav } from "../useSubStepNav";

/** The stored `YYYY-MM-DD`, read the way a person writes a date. */
function formatStartDate(value: string): string {
  const at = new Date(`${value}T00:00:00`);

  return Number.isNaN(at.getTime())
    ? value
    : at.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/** One sheet's contribution to the card badge. */
function statusOf(summary: OutstandingImportDTO | null): CardStatus {
  if (summary === null) return "not-started";
  if (summary.invalid_rows > 0 || summary.failed_rows > 0) return "needs-attention";

  return "uploaded";
}

export function OpeningBalancesStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "opening-balances");

  const [organisationName, setOrganisationName] = useState<string | null>(null);

  /**
   * Bumped by either panel whenever what is staged or pushed changes. It
   * re-reads the grid — whose locked AR/AP figures are derived from the
   * documents — and the badge fetches below.
   */
  const [revision, setRevision] = useState(0);
  const onChanged = useCallback(() => setRevision((value) => value + 1), []);

  /**
   * Whether the grid's debits and credits agree, reported by the grid as the
   * CA types. Starts true so a still-loading grid never blocks the button; the
   * first report lands with the sheet.
   */
  const [gridBalanced, setGridBalanced] = useState(true);

  /**
   * The two sheets' summaries, fetched by the step for the card badge alone.
   * The panels hold their own richer copies; this pair only answers "does the
   * card need attention", which has to survive either panel being mid-flow.
   */
  const [imports, setImports] = useState<Record<OutstandingKind, OutstandingImportDTO | null>>({
    invoice: null,
    bill: null,
  });

  useEffect(() => {
    let live = true;

    // Zoho's own name for the organisation, for the push buttons — "which
    // books am I writing to" is the question a push button answers.
    fetchZohoRecords(clientId, ["organisation"])
      .then((records) => {
        if (live) setOrganisationName(records.organisation?.name ?? null);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [clientId]);

  useEffect(() => {
    let live = true;

    const load = (kind: OutstandingKind) =>
      fetchOutstandingImport(clientId, kind)
        .then((review) => {
          if (live) setImports((current) => ({ ...current, [kind]: review.import }));
        })
        .catch(() => undefined);

    void load("invoice");
    void load("bill");

    return () => {
      live = false;
    };
  }, [clientId, revision]);

  if (!client) return null;

  const clientName = client.name || "this client";

  const invoiceStatus = statusOf(imports.invoice);
  const billStatus = statusOf(imports.bill);
  const cardStatus: CardStatus =
    invoiceStatus === "needs-attention" || billStatus === "needs-attention"
      ? "needs-attention"
      : invoiceStatus === "uploaded" || billStatus === "uploaded"
        ? "uploaded"
        : "not-started";

  // Not uploading anything is allowed — a new company has no outstanding
  // documents — but rows that failed or arrived invalid are known defects, and
  // moving on past those is how they end up in the first month-close instead.
  // Likewise a trial balance that does not tally: carrying an out-of-balance
  // sheet forward just moves the difference into the first month-close.
  const needsAttention = cardStatus === "needs-attention";

  return (
    <SubStepShell
      title="Set Up Opening Balances"
      description="Opening balances will be set up as of the Account Start Date shown below."
      thenWhat={
        needsAttention
          ? "Fix the rows marked needs-attention to continue."
          : !gridBalanced
            ? "Debits and credits must agree in the ledger balances to continue."
            : isBooksConnected(client)
              ? "Continue → Setting Up Zoho."
              : "Continue → Assign Your Team."
      }
      onContinue={() => void goNext()}
      continueDisabled={needsAttention || !gridBalanced}
    >
      {/*
        The date everything on this page is measured against, said once and
        loudly. It is chosen three steps earlier and every figure below is "as
        of" it, so reading the page without it in view is guesswork.
      */}
      <div className="flex flex-col gap-1 rounded-lg border-2 border-primary/30 bg-primary/5 px-5 py-4">
        <span className="text-xs font-medium uppercase tracking-wide text-primary">Account Start Date</span>
        <span className="text-2xl font-semibold tabular-nums">
          {client.accountStartDate ? formatStartDate(client.accountStartDate) : "Not set yet"}
        </span>
      </div>

      {/*
        FIRST, because this is the order the work happens in: the outstanding
        documents are what the receivable and payable figures are made of, so
        they go in before the trial balance that has to agree with them. Never
        optional: a client with no unpaid invoice and no unpaid bill on the
        start date is not a client with an opening balance problem, and the two
        uploads are one card because the double entry only makes sense as a
        pair.
      */}
      <StatusCard
        title="Outstanding invoices & bills"
        description="Upload all outstanding customer invoices and vendor bills dated before the Account Start Date."
        status={cardStatus}
      >
        <div className="flex flex-col gap-3">
          <OutstandingImportPanel
            clientId={clientId}
            clientName={clientName}
            organisationName={organisationName}
            kind="invoice"
            onChanged={onChanged}
          />
          <OutstandingImportPanel
            clientId={clientId}
            clientName={clientName}
            organisationName={organisationName}
            kind="bill"
            onChanged={onChanged}
          />
        </div>
      </StatusCard>

      {/*
        No status badge and no skip: its own footer says whether Zoho holds an
        opening balance and whether the entry balances, which is more than a
        badge could, and "not applicable" is not a thing an opening balance is.

        `reloadToken` ties it to the panels above: once documents are staged,
        the AR/AP cells up here lock into totals derived from them.
      */}
      {/*
        THE ONE CARD THAT ESCAPES THE PAGE WIDTH. Every other card on this step,
        and every other step, stays at the layout's `max-w-2xl`. This one holds a
        three-column grid whose two amount columns are sized to a crore-scale
        figure, and at 672px the name column is the only thing left to take width
        from. The negative margin widens the card into the gutter the centred
        container leaves either side, rather than moving the cap for the whole
        flow.
        Two steps because the gutter is not wide enough for one. `main`'s content
        box is 726px at 1366px wide, so there is 27px a side to borrow — `-mx-6`
        takes 24 of it and lands the card on 720px. The 768px the ticket asks for
        needs 48 a side, which first exists at 1440px (64px a side). Asking for
        768px at 1366px would have pushed the card out through `main`'s padding
        and into the nav.
      */}
      <StatusCard
        title="Ledger balances"
        description="Enter the opening balance for each ledger as of the Account Start Date."
        className="min-[1366px]:-mx-6 min-[1440px]:-mx-12"
      >
        {/* The bulk upload goes through the grid's own slot rather than above
            it: it belongs after receivable and payable — which are derived, not
            typed in bulk — and immediately before the groups it fills in.
            `onApplied` bumps the same `revision` the outstanding panels bump, so
            the grid re-reads rather than being told what changed. */}
        <OpeningBalanceGrid
          clientId={clientId}
          reloadToken={revision}
          onBalancedChange={setGridBalanced}
          beforeGroups={
            <OpeningBalanceImportPanel clientId={clientId} clientName={clientName} onApplied={onChanged} />
          }
        />
      </StatusCard>
    </SubStepShell>
  );
}
